import {
  AppleIcon,
  BabyIcon,
  BoneIcon,
  BookCheckIcon,
  CarIcon,
  CarrotIcon,
  DumbbellIcon,
  EarthIcon,
  ForkKnifeIcon,
  HammerIcon,
  HeartPulseIcon,
  HotelIcon,
  MapPinHouseIcon,
  PaintbrushIcon,
  ScanHeartIcon,
  ShieldPlusIcon,
  StoreIcon,
  TheaterIcon,
} from "lucide-react";

import { strictKeys } from "@/web/lib/objects";

export const BUSINESS_CATEGORIES_AND_TYPES = {
  accommodation: {
    friendlyName: "Accommodation",
    emoji: "🏨",
    hotel: { friendlyName: "Hotel" },
    motel: { friendlyName: "Motel" },
    bedAndBreakfast: { friendlyName: "Bed & breakfast" },
    extendedStay: { friendlyName: "Extended stay" },
    rvPark: { friendlyName: "RV park" },
    venue: { friendlyName: "Venue" },
    icon: HotelIcon,
  } as const,
  agriculture: {
    friendlyName: "Agriculture",
    emoji: "🌾",
    farm: { friendlyName: "Farm" },
    winery: { friendlyName: "Winery" },
    market: { friendlyName: "Market" },
    agritourism: { friendlyName: "Agritourism" },
    icon: CarrotIcon,
  } as const,
  automotive: {
    friendlyName: "Automotive",
    emoji: "🚗",
    carDealer: { friendlyName: "Car dealer" },
    carRepair: { friendlyName: "Car repair" },
    carWash: { friendlyName: "Car wash" },
    carRental: { friendlyName: "Car rental" },
    gasStation: { friendlyName: "Gas station" },
    autoPartsStore: { friendlyName: "Auto parts store" },
    icon: CarIcon,
  } as const,
  artAndCulture: {
    friendlyName: "Art & Culture",
    emoji: "🎨",
    icon: PaintbrushIcon,
    artGallery: { friendlyName: "Art gallery" },
    artStudio: { friendlyName: "Art studio" },
    museum: { friendlyName: "Museum" },
    auditorium: { friendlyName: "Auditorium" },
    performingArtsCenter: { friendlyName: "Performing arts center" },
  } as const,
  entertainment: {
    friendlyName: "Entertainment",
    emoji: "🎉",
    icon: TheaterIcon,
    amusementCenter: { friendlyName: "Amusement center" },
    amusementPark: { friendlyName: "Amusement park" },
    bowlingAlley: { friendlyName: "Bowling alley" },
    casino: { friendlyName: "Casino" },
    concertHall: { friendlyName: "Concert hall" },
    movieTheater: { friendlyName: "Movie theater" },
    nightclub: { friendlyName: "Nightclub" },
  } as const,
  buildingAndHomeServices: {
    friendlyName: "Building & home services",
    emoji: "🏠",
    electrician: { friendlyName: "Electrician" },
    plumber: { friendlyName: "Plumber" },
    painter: { friendlyName: "Painter" },
    roofing: { friendlyName: "Roofing" },
    landscaping: { friendlyName: "Landscaping" },
    cleaningServices: { friendlyName: "Cleaning services" },
    pestControl: { friendlyName: "Pest control" },
    homeSecurity: { friendlyName: "Home security" },
    generalContractor: { friendlyName: "General contractor" },
    handymanServices: { friendlyName: "Handyman services" },
    locksmith: { friendlyName: "Locksmith" },
    movingAndStorage: { friendlyName: "Moving & storage" },
    icon: HammerIcon,
  } as const,
  childCare: {
    friendlyName: "Child care",
    emoji: "👶",
    preschool: { friendlyName: "Preschool" },
    dayCare: { friendlyName: "Day care" },
    childCareAgency: { friendlyName: "Child care agency" },
    learningCenter: { friendlyName: "Learning center" },
    summerCamp: { friendlyName: "Summer camp" },
    icon: BabyIcon,
  } as const,
  education: {
    friendlyName: "Education",
    emoji: "🎓",
    tutoring: { friendlyName: "Tutoring" },
    music: { friendlyName: "Music" },
    artStudio: { friendlyName: "Art studio" },
    danceStudio: { friendlyName: "Dance studio" },
    vocationalTraining: { friendlyName: "Vocational training" },
    icon: BookCheckIcon,
  } as const,
  fitnessAndRecreation: {
    friendlyName: "Fitness & recreation",
    emoji: "🏋️",
    gym: { friendlyName: "Gym" },
    fitnessCenter: { friendlyName: "Fitness center" },
    sportsClub: { friendlyName: "Sports club" },
    swimmingPool: { friendlyName: "Swimming pool" },
    bowlingAlley: { friendlyName: "Bowling alley" },
    miniGolf: { friendlyName: "Mini golf" },
    golfCourse: { friendlyName: "Golf course" },
    skiResort: { friendlyName: "Ski resort" },
    martialArts: { friendlyName: "Martial arts" },
    yogaStudio: { friendlyName: "Yoga studio" },
    icon: DumbbellIcon,
  } as const,
  foodAndBeverage: {
    friendlyName: "Food & beverage",
    emoji: "🍔",
    restaurant: { friendlyName: "Restaurant" },
    catering: { friendlyName: "Catering" },
    coffeeShop: { friendlyName: "Coffee shop" },
    bakery: { friendlyName: "Bakery" },
    iceCreamShop: { friendlyName: "Ice cream shop" },
    pizzaShop: { friendlyName: "Pizza shop" },
    sandwichShop: { friendlyName: "Sandwich shop" },
    deli: { friendlyName: "Deli" },
    bar: { friendlyName: "Bar" },
    barbecue: { friendlyName: "Barbecue" },
    breakfastRestaurant: { friendlyName: "Breakfast restaurant" },
    vegetarianRestaurant: { friendlyName: "Vegetarian restaurant" },
    juiceBar: { friendlyName: "Juice bar" },
    icon: ForkKnifeIcon,
  } as const,
  groceryAndConvenience: {
    friendlyName: "Grocery & convenience",
    emoji: "🛒",
    groceryStore: { friendlyName: "Grocery store" },
    convenienceStore: { friendlyName: "Convenience store" },
    specialtyMarket: { friendlyName: "Specialty market" },
    liquorStore: { friendlyName: "Liquor store" },
    discountStore: { friendlyName: "Discount store" },
    departmentStore: { friendlyName: "Department store" },
    icon: AppleIcon,
  } as const,
  medical: {
    friendlyName: "Medical",
    emoji: "🏥",
    dentist: { friendlyName: "Dentist" },
    doctor: { friendlyName: "Doctor" },
    medicalLab: { friendlyName: "Medical lab" },
    pharmacy: { friendlyName: "Pharmacy" },
    physiotherapist: { friendlyName: "Physiotherapist" },
    icon: HeartPulseIcon,
  } as const,
  personalCare: {
    friendlyName: "Personal care",
    emoji: "💆",
    spa: { friendlyName: "Spa" },
    hairCare: { friendlyName: "Hair care" },
    beautyCare: { friendlyName: "Beauty care" },
    icon: ScanHeartIcon,
  } as const,
  petCare: {
    friendlyName: "Pet care",
    emoji: "🐶",
    veterinaryClinic: { friendlyName: "Veterinary clinic" },
    petStore: { friendlyName: "Pet store" },
    grooming: { friendlyName: "Grooming" },
    boarding: { friendlyName: "Boarding" },
    icon: BoneIcon,
  } as const,
  professionalServices: {
    friendlyName: "Professional services",
    emoji: "💼",
    accountant: { friendlyName: "Accountant" },
    architect: { friendlyName: "Architect" },
    consultant: { friendlyName: "Consultant" },
    designer: { friendlyName: "Designer" },
    lawyer: { friendlyName: "Lawyer" },
    insurance: { friendlyName: "Insurance" },
    marketing: { friendlyName: "Marketing" },
    realEstate: { friendlyName: "Real estate" },
    technology: { friendlyName: "Technology" },
    travel: { friendlyName: "Travel" },
    wholesaler: { friendlyName: "Wholesaler" },
    icon: ShieldPlusIcon,
  } as const,
  localServices: {
    friendlyName: "Local services",
    emoji: "🧺",
    dryCleaner: { friendlyName: "Dry cleaner" },
    laundry: { friendlyName: "Laundry" },
    florist: { friendlyName: "Florist" },
    tailor: { friendlyName: "Tailor" },
    printing: { friendlyName: "Printing" },
    photography: { friendlyName: "Photography" },
    movingServices: { friendlyName: "Moving services" },
    courier: { friendlyName: "Courier" },
    icon: MapPinHouseIcon,
  } as const,
  retail: {
    friendlyName: "Retail",
    emoji: "🛍️",
    bicycleShop: { friendlyName: "Bicycle shop" },
    clothingStore: { friendlyName: "Clothing store" },
    shoeStore: { friendlyName: "Shoe store" },
    jewelryStore: { friendlyName: "Jewelry store" },
    giftShop: { friendlyName: "Gift shop" },
    furnitureStore: { friendlyName: "Furniture store" },
    homeGoodsStore: { friendlyName: "Home goods store" },
    electronicsStore: { friendlyName: "Electronics store" },
    sportingGoodsStore: { friendlyName: "Sporting goods store" },
    bookstore: { friendlyName: "Bookstore" },
    hardwareStore: { friendlyName: "Hardware store" },
    store: { friendlyName: "Store" },
    shoppingMall: { friendlyName: "Shopping mall" },
    icon: StoreIcon,
  } as const,
  other: {
    friendlyName: "Other",
    emoji: "🤷",
    other: { friendlyName: "Other", emoji: "🤷" },
    icon: EarthIcon,
  } as const,
} as const;

export type BusinessCategory = keyof typeof BUSINESS_CATEGORIES_AND_TYPES;
export const ALL_BUSINESS_CATEGORIES = strictKeys(BUSINESS_CATEGORIES_AND_TYPES);

type TypeKeysForCategory<T> = {
  [K in keyof T]: K extends "friendlyName" | "emoji" | "icon" ? never : K;
}[keyof T];

type PrefixedTypesForCategory<
  CategoryKey extends BusinessCategory,
  Category = (typeof BUSINESS_CATEGORIES_AND_TYPES)[CategoryKey],
> = `${CategoryKey}.${TypeKeysForCategory<Category> & string}`;

export type BusinessCategoryType = {
  [K in BusinessCategory]: PrefixedTypesForCategory<K>;
}[BusinessCategory];

function getCategoryTypeMap(): Record<string, { friendlyName: string }> {
  const result: Record<string, { friendlyName: string }> = {};
  for (const [categoryKey, categoryData] of Object.entries(BUSINESS_CATEGORIES_AND_TYPES)) {
    for (const [typeKey, typeData] of Object.entries(categoryData)) {
      if (typeKey !== "friendlyName" && typeKey !== "emoji" && typeKey !== "icon") {
        result[`${categoryKey}.${typeKey}`] = typeData as { friendlyName: string };
      }
    }
  }
  return result;
}

const BUSINESS_CATEGORY_TYPE_MAP = getCategoryTypeMap();

export function getFriendlyName(type: BusinessCategoryType | undefined): string {
  if (!type) return "Business";
  return BUSINESS_CATEGORY_TYPE_MAP[type]?.friendlyName ?? "Business";
}

// ---------------------------------------------------------------------------
// Business tags
//
// As of the PROD-755 refactor there is exactly ONE canonical tag list
// (`CANONICAL_BUSINESS_TAGS` below). The three legacy lists
// (`BUSINESS_OWNER_DEMOGRAPHICS` / `BUSINESS_MANAGEMENT_STYLE` /
// `BUSINESS_PHILOSOPHY`) and their merged `BUSINESS_TAGS` are retained only so
// `getTagMetadata` can still render a friendly label for brand rows persisted
// with the OLD slugs. New writes always use canonical slugs.
// ---------------------------------------------------------------------------

export type BusinessTagClass = "demographic" | "businessAttribute";

// Source of truth for business tags. Alphabetized by slug.
// NOTE: the SpacetimeDB server keeps a hand-synced copy of these SLUGS only
// (apps/spacetimedb/src/tables/brands.ts) — never import across the
// client/server boundary.
export const CANONICAL_BUSINESS_TAGS = {
  asian: { friendlyName: "Asian", emoji: "🥢", class: "demographic" },
  black: { friendlyName: "Black", emoji: "✊🏾", class: "demographic" },
  bootstrapped: { friendlyName: "Bootstrapped", emoji: "🥾", class: "businessAttribute" },
  "downtown-association": {
    friendlyName: "Downtown association",
    emoji: "🏙️",
    class: "businessAttribute",
  },
  "family-business": { friendlyName: "Family business", emoji: "🧑‍🧑‍🧒‍🧒", class: "businessAttribute" },
  hospitality: { friendlyName: "Hospitality", emoji: "🛎️", class: "businessAttribute" },
  immigrant: { friendlyName: "Immigrant", emoji: "🌍", class: "demographic" },
  latinx: { friendlyName: "Latinx", emoji: "💃🏽", class: "demographic" },
  lgbtq: { friendlyName: "LGBTQ+", emoji: "🏳️‍🌈", class: "demographic" },
  "little-league": { friendlyName: "Little league", emoji: "⚾", class: "businessAttribute" },
  "local-chamber": { friendlyName: "Local chamber", emoji: "🤝", class: "businessAttribute" },
  "made-in-usa": { friendlyName: "Made in USA", emoji: "🇺🇸", class: "businessAttribute" },
  "main-street": { friendlyName: "Main street", emoji: "🛣️", class: "businessAttribute" },
  "multi-generation": { friendlyName: "Multi-generation", emoji: "👵", class: "demographic" },
  "serial-entrepreneur": { friendlyName: "Serial entrepreneur", emoji: "👔", class: "demographic" },
  "under-35": { friendlyName: "Under 35", emoji: "🧗🏾", class: "demographic" },
  veteran: { friendlyName: "Veteran", emoji: "🪖", class: "demographic" },
  women: { friendlyName: "Women", emoji: "👩", class: "demographic" },
} as const satisfies Record<
  string,
  { friendlyName: string; emoji: string; class: BusinessTagClass }
>;

export type CanonicalBusinessTag = keyof typeof CANONICAL_BUSINESS_TAGS;
export const ALL_CANONICAL_BUSINESS_TAGS = strictKeys(CANONICAL_BUSINESS_TAGS);

/** @deprecated use {@link CANONICAL_BUSINESS_TAGS}. Retained for `getTagMetadata`'s legacy fallback. */
export const BUSINESS_OWNER_DEMOGRAPHICS = {
  womenOwned: { friendlyName: "Women-owned", emoji: "👩" },
  latinxOwned: { friendlyName: "Latinx-owned", emoji: "💃🏽" },
  asianOwned: { friendlyName: "Asian-owned", emoji: "🥢" },
  blackOwned: { friendlyName: "Black-owned", emoji: "✊🏾" },
  lgbtq: { friendlyName: "LGBTQ+", emoji: "🏳️‍🌈" },
  disabledOwned: { friendlyName: "Disabled-owned", emoji: "🦿" },
  veteranOwned: { friendlyName: "Veteran-owned", emoji: "🪖" },
  immigrantOwned: { friendlyName: "Immigrant-owned", emoji: "🌍" },
  under35: { friendlyName: "Under 35", emoji: "🧗🏾" },
  serialEntrepreneur: { friendlyName: "Serial Entreprenuer", emoji: "👔" },
  firstTimeOwner: { friendlyName: "First-time Owner", emoji: "🐣" },
  firstGenerationOwner: { friendlyName: "First-Generation owner", emoji: "👣" },
} as const;

/** @deprecated use {@link CANONICAL_BUSINESS_TAGS}. Retained for `getTagMetadata`'s legacy fallback. */
export const BUSINESS_MANAGEMENT_STYLE = {
  momAndPop: { friendlyName: "True Mom & Pop", emoji: "👫" },
  ownerOperated: { friendlyName: "Owner-operated", emoji: "👨‍💻" },
  familyBusiness: { friendlyName: "Family Business", emoji: "🧑‍🧑‍🧒‍🧒" },
  generationalBusiness: { friendlyName: "Generational Business", emoji: "👵" },
  bootstrapped: { friendlyName: "Bootstrapped", emoji: "🥾" },
  singleLocation: { friendlyName: "Single Location", emoji: "🥇" },
  multiLocation: { friendlyName: "Multi-Location", emoji: "🏢" },
} as const;

/** @deprecated use {@link CANONICAL_BUSINESS_TAGS}. Retained for `getTagMetadata`'s legacy fallback. */
export const BUSINESS_PHILOSOPHY = {
  americanMade: { friendlyName: "Made in USA", emoji: "🇺🇸" },
  hospitality: { friendlyName: "Hospitality", emoji: "🛎️" },
  experientialRetail: { friendlyName: "Experiential Retail", emoji: "🧑‍🔬" },
  ecoFriendly: { friendlyName: "Eco-Friendly", emoji: "🌿" },
  artisanal: { friendlyName: "Artisanal", emoji: "🎨" },
  digitallyNative: { friendlyName: "Digitally Native", emoji: "💻" },
  luxury: { friendlyName: "Luxury", emoji: "💎" },
  valueDriven: { friendlyName: "Value-Driven", emoji: "✨" },
  culturalHeritage: { friendlyName: "Cultural Heritage", emoji: "🎭" },
} as const;

/** @deprecated use {@link CANONICAL_BUSINESS_TAGS}. Merged legacy list; only kept for legacy slug resolution. */
export const BUSINESS_TAGS = {
  ...BUSINESS_OWNER_DEMOGRAPHICS,
  ...BUSINESS_MANAGEMENT_STYLE,
  ...BUSINESS_PHILOSOPHY,
} as const;

/** @deprecated use {@link CanonicalBusinessTag} */
export type LegacyBusinessTag = keyof typeof BUSINESS_TAGS;
/** @deprecated use {@link CanonicalBusinessTag} */
export type BusinessTag = LegacyBusinessTag;

// Best-effort map from old persisted slugs → new canonical slugs, used by
// `getTagMetadata` so brands saved before the refactor still render a sensible
// label. Old slugs with no good canonical home (disabledOwned, firstTimeOwner,
// firstGenerationOwner, momAndPop, ownerOperated, singleLocation, multiLocation,
// experientialRetail, ecoFriendly, artisanal, digitallyNative, luxury,
// valueDriven, culturalHeritage) are intentionally dropped — they fall through
// to the legacy fallback in `getTagMetadata`.
// NOTE: slugs that are spelled identically in both lists (lgbtq, bootstrapped,
// hospitality) are intentionally absent — `getTagMetadata`'s canonical
// fast-path catches them in step 1, so mapping them here would be dead code.
const LEGACY_TO_CANONICAL_TAG = {
  womenOwned: "women",
  latinxOwned: "latinx",
  asianOwned: "asian",
  blackOwned: "black",
  veteranOwned: "veteran",
  immigrantOwned: "immigrant",
  under35: "under-35",
  serialEntrepreneur: "serial-entrepreneur",
  familyBusiness: "family-business",
  generationalBusiness: "multi-generation",
  americanMade: "made-in-usa",
} as const satisfies Partial<Record<LegacyBusinessTag, CanonicalBusinessTag>>;

export type TagMetadata = {
  friendlyName: string;
  emoji: string;
  class: BusinessTagClass | undefined;
  tag: string;
};

/**
 * Resolve metadata for ANY persisted tag slug (callers pass DB-sourced strings,
 * which may be canonical or legacy). Resolution is 4-tier, in order:
 *   1. canonical fast-path — slug is a current canonical tag.
 *   2. legacy-mapped — slug is an old slug with a canonical equivalent; return
 *      the mapped canonical entry (canonical label/emoji/class).
 *   3. legacy fallback — slug is an old `BUSINESS_TAGS` key with no canonical
 *      mapping; return its old label/emoji with `class: undefined`.
 *   4. unknown — return a safe generic default.
 */
export function getTagMetadata(tag: string): TagMetadata {
  // 1. canonical fast-path
  if (tag in CANONICAL_BUSINESS_TAGS) {
    const meta = CANONICAL_BUSINESS_TAGS[tag as CanonicalBusinessTag];
    return { ...meta, tag };
  }
  // 2. legacy-mapped → resolve to the canonical entry
  if (tag in LEGACY_TO_CANONICAL_TAG) {
    const canonical = LEGACY_TO_CANONICAL_TAG[tag as keyof typeof LEGACY_TO_CANONICAL_TAG];
    const meta = CANONICAL_BUSINESS_TAGS[canonical];
    return { ...meta, tag };
  }
  // 3. legacy fallback → old label/emoji, no class
  if (tag in BUSINESS_TAGS) {
    const meta = BUSINESS_TAGS[tag as LegacyBusinessTag];
    return { ...meta, class: undefined, tag };
  }
  // 4. unknown slug → safe default
  return { friendlyName: "Business", emoji: "🏷️", class: undefined, tag };
}

/**
 * Normalize a list of persisted tag slugs (which may be canonical OR legacy)
 * to canonical slugs, for re-hydrating the onboarding picker. Canonical slugs
 * pass through; legacy slugs with a canonical equivalent are mapped; legacy
 * slugs with no canonical home (and unknown slugs) are dropped. Order is
 * preserved and duplicates produced by mapping are de-duped.
 *
 * Why this matters: re-opening the wizard casts persisted slugs into the
 * picker's selected set. Without this mapping, a brand saved with old slugs
 * (e.g. `womenOwned`) would render every tag unchecked, and a "continue" press
 * would silently clear those tags from the brand. Mapping first means the
 * equivalent canonical tags show as selected and survive a re-save.
 */
export function toCanonicalBusinessTags(slugs: readonly string[]): CanonicalBusinessTag[] {
  const out: CanonicalBusinessTag[] = [];
  const seen = new Set<CanonicalBusinessTag>();
  for (const slug of slugs) {
    let canonical: CanonicalBusinessTag | undefined;
    if (slug in CANONICAL_BUSINESS_TAGS) {
      canonical = slug as CanonicalBusinessTag;
    } else if (slug in LEGACY_TO_CANONICAL_TAG) {
      canonical = LEGACY_TO_CANONICAL_TAG[slug as keyof typeof LEGACY_TO_CANONICAL_TAG];
    }
    if (canonical && !seen.has(canonical)) {
      seen.add(canonical);
      out.push(canonical);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// BusinessType — alias for BusinessCategoryType (leaf type strings)
// ---------------------------------------------------------------------------

export type BusinessType = BusinessCategoryType;

export const ALL_BUSINESS_TYPES: BusinessCategoryType[] = Object.keys(
  BUSINESS_CATEGORY_TYPE_MAP,
) as BusinessCategoryType[];

export function getBusinessTypeMetadata(type: BusinessCategoryType): {
  friendlyName: string;
  type: BusinessCategoryType;
} {
  const meta = BUSINESS_CATEGORY_TYPE_MAP[type];
  return { friendlyName: meta?.friendlyName ?? "Business", type };
}

export function getCategory(type: BusinessCategoryType): BusinessCategory {
  return type.split(".")[0] as BusinessCategory;
}
